import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveBar } from '@nivo/bar';
import { Tooltip as InfoHint } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import theme from '../../theme';

import { getFranchiseSales } from '../../api/dashboard';
import { MONTH_FULL_LIST } from '../../constants';
import { BRANCH_SALES_GRAPH_DESCRIPTION } from '../../constants/graphDescriptions';

const SALES_KEYS = ['Sales'];

// Custom nivo layer: a "% of monthly Target Sales" label on top of each Sales bar.
const SalesPercentLayer = ({ bars }: any) => (
  <g>
    {(bars || [])
      .filter((bar: any) => bar && bar.data && bar.data.id === 'Sales')
      .map((bar: any) => {
        const row = (bar.data && bar.data.data) || {};
        const target = Number(row['Target Sales']) || 0;
        if (target <= 0 || bar.width <= 0) {
          return null;
        }
        const pct = Math.round(((Number(row.Sales) || 0) / target) * 100);
        return (
          <text
            key={bar.key}
            x={bar.x + bar.width / 2}
            y={bar.y - 5}
            textAnchor="middle"
            style={{
              fontSize: 9,
              fontWeight: 700,
              fill: '#0b6b9b',
              pointerEvents: 'none',
            }}
          >
            {`${pct}%`}
          </text>
        );
      })}
  </g>
);

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const shortCurrency = (v: number) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (n === 0) {
    return '$0';
  }
  if (abs >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `$${(n / 1_000).toFixed(0)}K`;
  }
  return `$${Math.round(n)}`;
};

const niceMax = (maxValue: number) => {
  if (!maxValue || maxValue <= 0) {
    return 10;
  }
  const padded = maxValue * 1.08;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const step = magnitude / 2;
  return Math.ceil(padded / step) * step;
};

const chartTheme = {
  grid: {
    line: {
      stroke: '#E2E8F0',
      strokeDasharray: '2 4',
    },
  },
  axis: {
    ticks: {
      text: {
        fill: '#64748B',
        fontSize: 11,
      },
    },
  },
  legends: {
    text: {
      fill: '#334155',
      fontSize: 12,
    },
  },
  tooltip: {
    container: {
      background: '#ffffff',
      color: '#111827',
      fontSize: 12,
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
      padding: '8px 12px',
    },
  },
};

interface MonthRow {
  month: number;
  year: number;
  sales: number;
  projectedSales: number;
}

type SalesStatus = 'ahead' | 'on-track' | 'behind' | 'none';

const STATUS_LABEL: Record<SalesStatus, string> = {
  ahead: 'Ahead of target',
  'on-track': 'On track',
  behind: 'Behind target',
  none: 'No target set',
};

const FAFranchiseSalesGraph = () => {
  const [rows, setRows] = React.useState<MonthRow[]>([]);

  React.useEffect(() => {
    getFranchiseSales()
      .then(({ data }) => {
        setRows(
          (data || []).map((d: any) => ({
            month: Number(d.month),
            year: Number(d.year),
            sales: Number(d.sales) || 0,
            projectedSales: Number(d.projectedSales) || 0,
          })),
        );
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
  }, []);

  const barData = React.useMemo(
    () =>
      rows.map(d => ({
        label:
          MONTH_FULL_LIST[d.month - 1]?.slice(0, 3) ||
          String(d.month),
        Sales: d.sales,
        'Target Sales': d.projectedSales,
      })),
    [rows],
  );

  const hasData =
    rows.length > 0 &&
    rows.some(d => d.sales !== 0 || d.projectedSales !== 0);

  const maxY = React.useMemo(() => {
    const maxVal = rows.reduce(
      (acc, d) => Math.max(acc, d.sales, d.projectedSales),
      0,
    );
    return niceMax(maxVal);
  }, [rows]);

  const monthlyTarget = rows[0]?.projectedSales || 0;

  const summary = React.useMemo(() => {
    if (rows.length === 0) {
      return null;
    }
    const yearlyTarget = (rows[0]?.projectedSales || 0) * 12;
    const now = new Date();
    const monthsElapsed =
      rows[0]?.year === now.getFullYear()
        ? now.getMonth() + 1
        : 12;
    const ytdSales = rows
      .filter(d => d.month <= monthsElapsed)
      .reduce((sum, d) => sum + d.sales, 0);
    const expectedToDate = (yearlyTarget * monthsElapsed) / 12;
    let status: SalesStatus = 'none';
    if (yearlyTarget > 0) {
      const pace =
        expectedToDate > 0 ? ytdSales / expectedToDate : 1;
      if (pace >= 1.05) {
        status = 'ahead';
      } else if (pace >= 0.9) {
        status = 'on-track';
      } else {
        status = 'behind';
      }
    }
    const pctOfYear =
      yearlyTarget > 0
        ? Math.round((ytdSales / yearlyTarget) * 100)
        : null;
    return {
      year: rows[0]?.year ?? now.getFullYear(),
      yearlyTarget,
      ytdSales,
      pctOfYear,
      status,
    };
  }, [rows]);

  return (
    <Container>
      <Header>
        Branch Sales Graph
        <InfoHint
          arrow
          placement="top"
          title={BRANCH_SALES_GRAPH_DESCRIPTION}
        >
          <InfoIcon fontSize="small" />
        </InfoHint>
      </Header>

      {summary && (
        <StatusBanner>
          <BannerMain>
            <BannerEyebrow>
              {summary.year} Target Sales
            </BannerEyebrow>
            <BannerNumbers>
              <strong>
                {currencyFormat.format(summary.ytdSales)}
              </strong>{' '}
              of{' '}
              {currencyFormat.format(summary.yearlyTarget)}{' '}
              yearly target
              {summary.pctOfYear != null && (
                <Muted> · {summary.pctOfYear}% of goal</Muted>
              )}
            </BannerNumbers>
          </BannerMain>
          <StatusBadge data-status={summary.status}>
            {STATUS_LABEL[summary.status]}
          </StatusBadge>
        </StatusBanner>
      )}

      {hasData ? (
        <Bar>
          <ResponsiveBar
            data={barData}
            animate={false}
            enableLabel={false}
            keys={SALES_KEYS}
            indexBy="label"
            colors={theme.marker1}
            padding={0.4}
            borderRadius={3}
            valueScale={{ type: 'linear', max: maxY }}
            markers={
              monthlyTarget > 0
                ? [
                    {
                      axis: 'y',
                      value: monthlyTarget,
                      lineStyle: {
                        stroke: '#0b6b9b',
                        strokeWidth: 1.5,
                        strokeDasharray: '6 4',
                        strokeOpacity: 0.7,
                      },
                      legend: `Target Sales · ${currencyFormat.format(
                        monthlyTarget,
                      )}/mo`,
                      legendPosition: 'top-right',
                      legendOrientation: 'horizontal',
                      textStyle: {
                        fill: '#0b6b9b',
                        fontSize: 11,
                        fontWeight: 600,
                      },
                    },
                  ]
                : []
            }
            layers={[
              'grid',
              'axes',
              'bars',
              SalesPercentLayer,
              'markers',
              'legends',
              'annotations',
            ]}
            margin={{
              top: 40,
              right: 16,
              bottom: 44,
              left: 64,
            }}
            enableGridY
            theme={chartTheme}
            axisLeft={{
              tickPadding: 8,
              tickSize: 0,
              format: shortCurrency,
            }}
            axisBottom={{ tickPadding: 6, tickSize: 0 }}
            tooltip={({
              id,
              value,
              color,
              indexValue,
            }: any) => (
              <Tooltip>
                <TooltipDot
                  style={{ background: color as string }}
                />
                <TooltipLabel>
                  {indexValue} · {id}
                </TooltipLabel>
                <TooltipValue>
                  {currencyFormat.format(Number(value) || 0)}
                </TooltipValue>
              </Tooltip>
            )}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'top-left',
                direction: 'row',
                translateY: -24,
                translateX: 0,
                itemsSpacing: 16,
                itemWidth: 70,
                itemHeight: 18,
                symbolShape: 'square',
                symbolSize: 12,
                itemTextColor: '#334155',
              },
            ]}
          />
        </Bar>
      ) : (
        <EmptyState>No sales data available</EmptyState>
      )}
    </Container>
  );
};

const Container = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const Bar = styled.div`
  height: 400px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 16px;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: var(--neutral-400);
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;
`;

const BannerMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const BannerEyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const BannerNumbers = styled.div`
  font-size: 0.9375rem;
  color: var(--neutral-700);

  > strong {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--mainText);
  }
`;

const Muted = styled.span`
  color: var(--neutral-500);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  background: var(--neutral-100);
  color: var(--neutral-600);
  border: 1px solid var(--border);

  &[data-status='ahead'] {
    background: #dcfce7;
    color: #15803d;
    border-color: #bbf7d0;
  }
  &[data-status='on-track'] {
    background: var(--primary-50);
    color: var(--primary-700);
    border-color: var(--primary-100);
  }
  &[data-status='behind'] {
    background: var(--warning-50, #fff6e0);
    color: var(--warning-700, #8a5f05);
    border-color: var(--warning-100, #ffe7ac);
  }
`;

const Tooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const TooltipLabel = styled.span`
  color: #475569;
`;

const TooltipValue = styled.span`
  color: #0f172a;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

export default FAFranchiseSalesGraph;
